import React, { useCallback, useRef, useState } from "react";

import { Tooltip, useTheme } from "@mui/material";
import { Typography } from "@mui/material";
import ShipeeziNewGrid, {
  Column,
  DefaultDateOptionsColumn,
} from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import InputDateRange from "Components/Form/Input/DateRange";
import BadgeSubStatus from "Components/Generic/BadgeSubStatus";
import { DefaultTooltip } from "Components/Generic/Tooltip";
import SideViewComponent, { PropHandles } from "Components/Generic/ViewJobComponent";
import { DefaultPeriodOptionsColumn } from "Constants";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import {
  DocumentDownload,
  Task as TaskIcon,
  ExportSquare as RedirectIcon,
  Calendar,
  Sms,
} from "iconsax-react";
import { Branch } from "Models/branch";
import { Company } from "Models/company";
import { Customer } from "Models/customer";
import { OrdersManagement, OrdersManagementCSV } from "Models/report.orders.management";
import { Task } from "Models/task";
import { useTranslation } from "react-i18next";
import { capitalizeFirstCharacter } from "Util/Format";
import { jsonToCsv } from "Util/generateCsv";

import { Color } from "../../../../../../components/Form/Button";
import { Filters } from "../../../../../../models/shipeezi.table";
import { DateSelectorFilter, PeriodOptions } from "./components/DateSelector";
import { CronjobFrequency, CronJobType, ReportName } from "../../../../../../util/enums";
import { orderViewActions } from "./hooks/orderViewActions";
import { useActions } from "./hooks/useActions";
import {
  EmptyDiv,
  ExportCSVButton,
  GridActionContainer,
  GridActionItemContainer,
  GridWithAfterRowWrapper,
  OpenSubscriptionDrawIconButton,
  RightActionsGrid,
} from "./styles";
import { TasksGridAfterRow } from "./tasksGridAfterRow";
import { ManagementWidgets } from "./Widgets";
import { MangementWrapper } from "./Widgets/styles";
import {
  ManageEmailSubscriptionEditorDrawer,
  ManageEmailSubscriptionEditorDrawerHandles,
} from "Components/ManageEmailSubscriptionEditorDrawer";
import { useMutation } from "@tanstack/react-query";
import { ReportManagement } from "Util/queries";
import ReportManagementService from "Services/analytics.report.service";
import { Cronjob } from "Models/cronjob";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { AxiosError } from "axios";
import { useAppState } from "Context/StateContext";

export interface DateOption {
  name: string;
  column: string;
}

const today = new Date();

export const OrderAndTaskUsageReportScreen = () => {
  const {
    companies,
    useGetJobType,
    useGetJobStatus,
    useGetJobModes,
    getOrdersManagementsWidgets,
    getOrdersManagements,
    ordersManagementsReports,
    page,
    pageSize,
    setEndDate,
    setFilters,
    setPage,
    setPageSize,
    setStartDate,
    total,
    useExportOrderCSVData,
    branchesList,
    handleSearch,
    period,
    setPeriod,
    startDate,
    endDate,
    defaultStartDate,
    defaultEndDate,
    filters,
    setOpenRows,
    openRows,
  } = useActions();
  const {
    handleSetNavigation,
    getSidebarTitle,
    handleSetTabs,
    handleSetButtonsComponent,
    setOrderToView,
    loadingOrder,
  } = orderViewActions();
  const { t: translate } = useTranslation();
  const sideViewRef = useRef<PropHandles>(null);
  const theme = useTheme();
  const { currentUser } = useAppState();

  const manageEmailSubscriptionEditorDrawerRef =
    useRef<ManageEmailSubscriptionEditorDrawerHandles>(null);

  const { isFetching: loadingOrdersManagements } = getOrdersManagements();
  const { data: widgets, isFetching: loadingWidgets } = getOrdersManagementsWidgets();
  const { data: jobModes } = useGetJobModes();
  const { data: jobStatus } = useGetJobStatus();
  const { data: jobTypes } = useGetJobType();

  const getLastDaysLabel = (days: number) => {
    return `${capitalizeFirstCharacter(translate("last"))} ${days} ${translate(
      "days"
    ).toLocaleLowerCase()}`;
  };

  const periodOptions: PeriodOptions[] = [
    {
      name: getLastDaysLabel(7),
      column: DefaultPeriodOptionsColumn.LAST_WEEK,
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      totalDays: 7,
    },
    {
      name: getLastDaysLabel(30),
      column: DefaultPeriodOptionsColumn.LAST_MONTH,
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      totalDays: 30,
    },
    {
      name: getLastDaysLabel(90),
      column: DefaultPeriodOptionsColumn.LAST_QUARTER,
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      totalDays: 90,
    },
    {
      name: getLastDaysLabel(365),
      column: DefaultPeriodOptionsColumn.LAST_YEAR,
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      totalDays: 365,
    },
    {
      name: translate("lifetime"),
      column: DefaultPeriodOptionsColumn.LIFETIME,
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      description: translate("reportLifetimeData"),
    },
    {
      name: translate("dateRange"),
      column: DefaultPeriodOptionsColumn.CUSTOM_PERIOD,
      type: DefaultDateOptionsColumn.DATE_RANGE,
      icon: <Calendar size={12} color={colors.gray800} />,
    },
  ];

  const handleGenerateOrdersCSV = (data: OrdersManagementCSV[]) => {
    const formatedDate = format(
      utcToZonedTime(today, Intl.DateTimeFormat().resolvedOptions().timeZone),
      "dd_MM_yyyy"
    );

    const filename = `${formatedDate}_Order_and_task_usage_report_${currentUser?.company.name
      ?.split(" ")
      .join("_")}`;
    const extension = "csv";
    const fileNameWithExtension = `${filename}.${extension}`;
    const jsonString = `data:text/csv;chatset=utf-8,${encodeURIComponent(jsonToCsv(data))}`;
    const link = document.createElement("a");

    link.href = jsonString;
    link.download = fileNameWithExtension;
    document.body.appendChild(link);

    link.click();
    link.remove();
  };

  const { mutate: exportOrdersData, isLoading: isExportingCSV } =
    useExportOrderCSVData(handleGenerateOrdersCSV);

  const renderDownloadReportButton = () => {
    return (
      <RightActionsGrid
        container
        item
        direction="row"
        alignItems="flex-end"
        justifyContent="flex-end"
      >
        <DefaultTooltip title={translate("exportCSV")} placement="top">
          <div>
            <ExportCSVButton
              loading={isExportingCSV}
              customColor={Color.SECONDARY}
              disabled={isExportingCSV}
              onClick={() => exportOrdersData()}
              startIcon={<DocumentDownload size={20} color={colors.white} />}
            >
              {translate("exportCSV")}
            </ExportCSVButton>
          </div>
        </DefaultTooltip>
      </RightActionsGrid>
    );
  };
  const onChangePeriod = (period: DefaultPeriodOptionsColumn) => {
    setStartEndDates(period, startDate ?? defaultStartDate, endDate ?? defaultEndDate);
    setPeriod(period);
    setPage(0);
  };

  const renderSelectPeriodFilters = () => {
    return <DateSelectorFilter options={periodOptions} value={period} onChange={onChangePeriod} />;
  };

  const formatDate = (date: string) => {
    if (date) {
      return format(
        utcToZonedTime(date, Intl.DateTimeFormat().resolvedOptions().timeZone),
        "dd/MM/yyyy hh:mm a"
      );
    }

    return "-";
  };

  const columns: Column[] = [
    {
      name: "company",
      title: translate("company"),
      formatText: (company: Company) => company?.name,
      hideSearch: true,
    },
    {
      name: "branchFrom",
      hideSearch: true,
      title: translate("originBranch"),
      formatComponent: (branchFrom: Branch) => branchFrom?.name,
    },
    {
      name: "toBranch",
      hideSearch: true,
      title: translate("destinationBranch"),
      formatComponent: (branchTo: Branch) => branchTo?.name,
    },
    {
      name: "type",
      hideSearch: true,
      title: translate("type"),
    },
    {
      name: "reference",
      title: translate("reference"),
    },
    {
      name: "customer",
      title: translate("customer"),
      formatText: (customer: Customer) => customer?.fullName,
    },
    {
      name: "tasks",
      hideSearch: true,
      title: translate("noOfTasks"),
      formatText: (tasks: Task[]) => tasks?.length ?? 0,
    },
    {
      name: "createdAt",
      title: translate("orderCreated"),
      hideSearch: true,
      formatComponent: formatDate,
    },
    {
      name: "updatedAt",
      title: translate("lastUpdated"),
      hideSearch: true,
      formatComponent: formatDate,
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: string) => <BadgeSubStatus value={status} />,
      hideSearch: true,
    },
    {
      name: "id",
      title: translate("actions"),
      hideSearch: true,
      formatComponent: (_, order: OrdersManagement) => {
        return renderRowActions(order);
      },
    },
  ];

  const renderEmptyDiv = () => {
    return (
      <EmptyDiv>
        <Typography
          fontFamily="Manrope"
          fontWeight={400}
          fontSize="16px"
          color="#9CA6AF"
          lineHeight="18px"
          marginBottom="28px"
        >
          {translate("itFeelsEmptyRightNow")}
        </Typography>
      </EmptyDiv>
    );
  };

  const handleCurrentPage = (numPage: number) => {
    setPage(numPage - 1);
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const setStartEndDates = (period: string, startDate: Date, endDate: Date) => {
    switch (period) {
      case DefaultPeriodOptionsColumn.LAST_WEEK:
        setStartDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
        setEndDate(new Date());
        break;
      case DefaultPeriodOptionsColumn.LAST_MONTH:
        setStartDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
        setEndDate(new Date());
        break;
      case DefaultPeriodOptionsColumn.LAST_QUARTER:
        setStartDate(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000));
        setEndDate(new Date());
        break;
      case DefaultPeriodOptionsColumn.LAST_YEAR:
        setStartDate(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000));
        setEndDate(new Date());
        break;
      case DefaultPeriodOptionsColumn.LIFETIME:
        setStartDate(new Date(Date.now() - 20 * 365 * 24 * 60 * 60 * 1000));
        setEndDate(new Date());
        break;
      case DefaultPeriodOptionsColumn.CUSTOM_DATE:
        setStartDate(startDate || "");
        setEndDate(startDate || "");
        break;
      case DefaultPeriodOptionsColumn.CUSTOM_PERIOD:
        setStartDate(startDate || "");
        setEndDate(endDate || "");
        break;
      default:
        setStartDate(undefined);
        setEndDate(undefined);
    }
  };

  const toggleCollapse = useCallback((orderId?: number) => {
    if (orderId) {
      setOpenRows((prev) => {
        if (prev.find((item) => item === orderId)) {
          return prev.filter((item) => item !== orderId);
        }

        return [...prev, orderId];
      });
    }
  }, []);

  const renderShowTasksButton = (order: OrdersManagement) => {
    const orderId = order?.id;

    if (!order?.tasks?.length || !orderId) return null;

    const isOpen = !!openRows.find((item) => item === orderId);

    return (
      <DefaultTooltip title={translate(isOpen ? "hideTasks" : "showTasks")}>
        <GridActionItemContainer active={isOpen}>
          <TaskIcon color={colors.gray600} size={16} onClick={() => toggleCollapse(orderId)} />
        </GridActionItemContainer>
      </DefaultTooltip>
    );
  };

  const renderOpenOrderButton = (order: OrdersManagement) => {
    return (
      <DefaultTooltip title={translate("openOrder")}>
        <GridActionItemContainer>
          <RedirectIcon
            color={colors.gray600}
            size={16}
            onClick={() => {
              setOrderToView(order as any);
              sideViewRef.current?.setOpenSideBar(true);
            }}
          />
        </GridActionItemContainer>
      </DefaultTooltip>
    );
  };

  const renderRowActions = (order: OrdersManagement) => {
    return (
      <GridActionContainer>
        {renderOpenOrderButton(order)}
        {renderShowTasksButton(order)}
      </GridActionContainer>
    );
  };

  const getJobModes = () => {
    const jobModesExtracted: string[] = [];

    if (jobModes && jobModes.length > 0) {
      jobModes?.forEach((jobMode) => {
        if (jobMode && jobMode?.name) {
          jobModesExtracted.push(jobMode?.name);
        }
      });
    }

    return jobModesExtracted;
  };

  const getJobTypes = () => {
    const jobTypesExtracted: string[] = [];

    if (jobTypes && jobTypes.length > 0) {
      jobTypes?.forEach((jobType) => {
        if (jobType && jobType?.name) {
          jobTypesExtracted.push(jobType?.name);
        }
      });
    }

    return jobTypesExtracted;
  };

  const getStatuses = () => {
    const statusesExtracted: string[] = [];

    if (jobStatus && jobStatus.length > 0) {
      jobStatus?.forEach((status) => {
        if (status && status?.name) {
          statusesExtracted.push(status?.name);
        }
      });
    }

    const uniqueStatuses = Array.from(new Set(statusesExtracted));
    return uniqueStatuses;
  };

  const renderAfterRow = (orderManagement: OrdersManagement) => {
    const isOpen = !!(orderManagement?.id && openRows?.includes(orderManagement?.id));

    if (!isOpen) return null;

    return (
      <TasksGridAfterRow
        tasks={orderManagement.tasks.map((task) => ({
          ...task,
          branchFrom: orderManagement?.branchFrom,
        }))}
        loading={false}
      />
    );
  };

  const renderInputDateRange = () => {
    switch (period) {
      case DefaultPeriodOptionsColumn.CUSTOM_PERIOD:
        const getStartDate = () => {
          if (startDate === undefined) return new Date(today.getFullYear(), today.getMonth(), 1);
          if (startDate) return new Date(startDate);

          return undefined;
        };

        const getEndDate = () => {
          if (endDate === undefined) return today;
          if (endDate) return new Date(endDate);

          return undefined;
        };

        return (
          <InputDateRange
            pStartDate={getStartDate()}
            pEndDate={getEndDate()}
            onChange={(range) => {
              setStartDate(range?.startDate);
              setEndDate(range?.endDate);
            }}
          />
        );
      default:
        return null;
    }
  };

  const { mutate: createSubscription, isLoading: isCreatingCronJob } = useMutation({
    mutationKey: ReportManagement.deleteSubscription(),
    mutationFn: (variables: Cronjob) =>
      ReportManagementService.createManagement(variables, { ...filters, startDate, endDate }),
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      manageEmailSubscriptionEditorDrawerRef?.current?.close();
    },
    onError: (error: AxiosError<Error>) => {
      if (error && error.response) NotificationToast.error(error?.response?.data?.message);
    },
  });

  const renderHandleSubscriptionButton = () => {
    return (
      <DefaultTooltip title={translate("sendReportByEmail")}>
        <div>
          <OpenSubscriptionDrawIconButton
            onClick={() =>
              manageEmailSubscriptionEditorDrawerRef?.current?.open({
                filters: JSON.stringify({ ...filters, startDate, endDate }),
                frequency: CronjobFrequency.ONCE,
              } as Cronjob)
            }
          >
            <Sms size={21} color={theme.palette.primary.main} />
          </OpenSubscriptionDrawIconButton>
        </div>
      </DefaultTooltip>
    );
  };

  return (
    <>
      <MangementWrapper>
        <ManagementWidgets widgets={widgets} isLoading={loadingWidgets} />
        <GridWithAfterRowWrapper id="MainGrid">
          <ShipeeziNewGrid
            loadingData={loadingOrdersManagements}
            emptyData={() => renderEmptyDiv()}
            data={ordersManagementsReports}
            onChangePageSize={handlePageSize}
            onNextPage={handleCurrentPage}
            onPreviousPage={handleCurrentPage}
            onGoToPage={handleCurrentPage}
            columns={columns}
            totalItems={total}
            pageSize={pageSize}
            pageSizeList={[20, 40, 60, 100]}
            currentPage={page}
            showColumnChooser={false}
            showActionLabel={false}
            positionActionsColumn="right"
            showSelection={false}
            renderAfterRow={renderAfterRow}
            generalActions={{
              orderBy: {
                right: ["handleSubscriptions", "exportCSV"],
                left: ["handleSelectPeriod", "dateSelector", "handleSearch", "handleFilterBy"],
              },
              handleFilterBy: {
                categories: [
                  {
                    name: translate("type"),
                    column: "type",
                    options: getJobTypes(),
                  },
                  {
                    name: translate("company"),
                    column: "companyId",
                    options: companies?.map((company) => {
                      return {
                        label: company?.name ?? "",
                        value: company?.id ? String(company?.id) : "",
                      };
                    }),
                    optionsSelected: filters["companyId"] as string[],
                  },
                  {
                    name: translate("originBranch"),
                    column: "branchFromId",
                    options: branchesList?.map((branch: Branch) => {
                      return {
                        label: branch?.name ?? "",
                        value: branch?.id ? String(branch?.id) : "",
                      };
                    }),
                  },
                  {
                    name: translate("destinationBranch"),
                    column: "toBranchId",
                    options: branchesList?.map((branch: Branch) => {
                      return {
                        label: branch?.name ?? "",
                        value: branch?.id ? String(branch?.id) : "",
                      };
                    }),
                  },
                  {
                    name: translate("mode"),
                    column: "mode",
                    options: getJobModes(),
                  },
                  {
                    name: translate("status"),
                    column: "status",
                    options: getStatuses(),
                  },
                ],
                onChange: handleFilter,
              },
              handleSearch: {
                onSearch: handleSearch,
              },
              handleSubscriptions: renderHandleSubscriptionButton(),
              exportCSV: renderDownloadReportButton(),
              handleSelectPeriod: renderSelectPeriodFilters(),
              dateSelector: renderInputDateRange(),
            }}
          />
        </GridWithAfterRowWrapper>
      </MangementWrapper>
      <SideViewComponent
        ref={sideViewRef}
        isLoading={loadingOrder}
        title={getSidebarTitle()}
        navigation={handleSetNavigation()}
        tabs={handleSetTabs()}
        buttonsComponent={handleSetButtonsComponent()}
      />

      <ManageEmailSubscriptionEditorDrawer
        ref={manageEmailSubscriptionEditorDrawerRef}
        isLoading={isCreatingCronJob}
        submitCronJob={(cronJob) =>
          cronJob &&
          createSubscription({
            ...cronJob,
            type: CronJobType.ORDER_AND_TASK_REPORT,
            companyId: currentUser?.companyId,
            name: ReportName.ORDER_AND_TASK_USAGE_REPORT,
          })
        }
      />
    </>
  );
};
